import { useMemo } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import apiClient from '../lib/api'

// Prefetch hook - call on hover for instant loading
export function usePrefetch() {
  const queryClient = useQueryClient()

  return useMemo(() => ({
    prefetchGamePreview: (gameId, league = 'nba') => {
      if (!gameId) return
      queryClient.prefetchQuery({
        queryKey: ['gamePreview', league, gameId],
        queryFn: () => apiClient.getGamePreview(gameId, league),
        staleTime: 1000 * 60 * 60, // 1 hour
      })
    },
    prefetchPlayerStats: (playerId, league = 'nba') => {
      if (!playerId) return
      queryClient.prefetchQuery({
        queryKey: ['playerStats', league, playerId],
        queryFn: () => apiClient.getPlayerStats(playerId, league),
        staleTime: 1000 * 60 * 5,
      })
    },
    prefetchPulse: (league = 'nba') => {
      queryClient.prefetchQuery({
        queryKey: ['pulse', league],
        queryFn: () => apiClient.getPulse(league),
        staleTime: 1000 * 60, // 1 min
      })
    },
    // Prefetch all game previews for today's games
    prefetchAllPreviews: (games) => {
      if (!games?.length) return
      games.forEach(game => {
        const gameId = game?.raw?.id || game?.id
        const league = (game?.league || game?.raw?.league || 'nba').toString().toLowerCase()
        if (gameId) {
          queryClient.prefetchQuery({
            queryKey: ['gamePreview', league, gameId],
            queryFn: () => apiClient.getGamePreview(gameId, league),
            staleTime: 1000 * 60 * 60,
          })
        }
      })
    },
  }), [queryClient])
}

// Mock data for development
const mockGames = [
  {
    id: 'game1',
    league: 'NBA',
    status: 'LIVE',
    time: 'Q3 • 6:45',
    teams: [
      { name: 'Lakers', score: 72, id: 'lakers' },
      { name: 'Celtics', score: 68, id: 'celtics' }
    ]
  },
  {
    id: 'game2',
    league: 'NFL',
    status: 'LIVE',
    time: 'Q4 • 2:15',
    teams: [
      { name: 'Chiefs', score: 24, id: 'chiefs' },
      { name: 'Ravens', score: 21, id: 'ravens' }
    ]
  },
  {
    id: 'game3',
    league: 'Soccer',
    status: 'LIVE',
    time: "78'",
    teams: [
      { name: 'Man City', score: 2, id: 'mancity' },
      { name: 'Liverpool', score: 1, id: 'liverpool' }
    ]
  }
]

// Toggle this to switch between mock and real API
const USE_MOCK_DATA = false

export function useLiveGames(date = 'today', league = 'nba') {
  return useQuery({
    queryKey: ['liveGames', league, date],
    queryFn: async () => {
      if (USE_MOCK_DATA) {
        // Simulate network delay
        await new Promise(r => setTimeout(r, 300))
        return mockGames
      }
      try {
        console.log(`[useLiveGames] Fetching ${league.toUpperCase()} games for ${date}...`)
        const response = await apiClient.getLiveGames(date, league)
        const games = Array.isArray(response?.games) ? response.games : Array.isArray(response) ? response : []
        // Log merge success for debugging
        const withPlayers = games.filter(g => g.players?.home?.length > 0 || g.players?.away?.length > 0)
        console.log(`[useLiveGames] Loaded ${games.length} ${league.toUpperCase()} games, ${withPlayers.length} with player stats`)
        // Return games with data status for freshness display
        return {
          games,
          league: response?.league || league,
          dataStatus: response?.dataStatus || null,
          lastPoll: response?.dataStatus?.lastPoll || null,
        }
      } catch (err) {
        console.error(`[useLiveGames] Failed to fetch ${league} games:`, err)
        // Re-throw to let React Query handle retry
        throw err
      }
    },
    refetchInterval: date === 'today' ? 5000 : 120000, // Refresh live games every 5s for real-time feel
    staleTime: 3000, // Consider data stale after 3s
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 3, // Retry failed requests 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    // Don't use placeholderData for league switches - show loading state instead
    placeholderData: undefined,
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch on reconnect - we have interval
  })
}

export function useGameStats(gameId, league = 'nba', options = {}) {
  const { isLive = false } = options
  return useQuery({
    queryKey: ['gameStats', gameId, league],
    queryFn: () => apiClient.getGameStats(gameId, league),
    enabled: !!gameId, // Only fetch when gameId exists
    staleTime: 60000, // Stats don't change often, 1 min stale time
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 2,
    refetchInterval: isLive ? 10000 : false,
    refetchIntervalInBackground: isLive,
  })
}

export function useOdds() {
  return useQuery({
    queryKey: ['odds'],
    queryFn: () => apiClient.getOdds(),
    refetchInterval: 60000, // Refresh every 60s
    staleTime: 30000,
  })
}

export function useGameOdds(gameId) {
  return useQuery({
    queryKey: ['gameOdds', gameId],
    queryFn: () => apiClient.getGameOdds(gameId),
    enabled: !!gameId,
  })
}

export function usePlayerStats(playerId, league = 'nba') {
  return useQuery({
    queryKey: ['playerStats', league, playerId],
    queryFn: () => apiClient.getPlayerStats(playerId, league),
    enabled: !!playerId,
    staleTime: 1000 * 60 * 5, // 5 minutes - refresh frequently for latest games
  })
}

// Search player by name - fallback when ID doesn't work
export function usePlayerStatsByName(playerName, teamAbbr = null) {
  return useQuery({
    queryKey: ['playerStatsByName', playerName, teamAbbr],
    queryFn: () => apiClient.getPlayerStatsByName(playerName, teamAbbr),
    enabled: !!playerName,
    staleTime: 1000 * 60 * 5,
  })
}

export function useGamePreview(gameId, league = 'nba') {
  // Get cached games to use as placeholder while preview loads
  const { data: gamesData } = useLiveGames('today', league)
  
  return useQuery({
    queryKey: ['gamePreview', league, gameId],
    queryFn: () => apiClient.getGamePreview(gameId, league),
    enabled: !!gameId,
    staleTime: 0, // Always fetch fresh data
    gcTime: 1 * 60 * 1000, // 1 minute cache
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    // Use cached games as placeholder while preview loads
    placeholderData: () => {
      const game = gamesData?.games?.find(g => g.id === gameId)
      if (game) {
        return {
          gameId,
          datetime: game.datetime,
          status: game.status,
          homeTeam: {
            id: game.home_team?.id,
            name: game.home_team?.name,
            abbreviation: game.home_team?.abbreviation,
          },
          awayTeam: {
            id: game.visitor_team?.id,
            name: game.visitor_team?.name,
            abbreviation: game.visitor_team?.abbreviation,
          },
          odds: game.odds || null,
          playerProps: [],
          injuries: [],
          isPlaceholder: true,
        }
      }
      return undefined
    },
  })
}

// Pulse feed - injuries, news, line movements
export function usePulse(league = 'nba', teams = '') {
  return useQuery({
    queryKey: ['pulse', league, teams],
    queryFn: () => apiClient.getPulse(league, teams),
    refetchInterval: 30000, // Refresh every 30s for faster updates
    staleTime: 15000,
    placeholderData: { items: [], events: [], injuries: [], lineMovements: [] }, // Show empty state immediately (items is what PulseTab uses)
  })
}

// Market Movers - line movements for Signals tab (both NBA and NFL)
export function useMarketMovers(league = 'nba') {
  return useQuery({
    queryKey: ['marketMovers', league],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || 'https://orbglide-analyzer.orbglide.workers.dev'
      const res = await fetch(`${baseUrl}/api/market-movers?league=${league}`)
      if (!res.ok) return { movers: [] }
      return res.json()
    },
    staleTime: 60000, // 1 min
    refetchInterval: 120000, // 2 min
    placeholderData: { movers: [] },
  })
}

// Get player's season ranking (e.g., 3rd in points)
export function usePlayerRanking(playerId, statType = 'pts') {
  return useQuery({
    queryKey: ['playerRanking', playerId, statType],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || 'https://orbglide-analyzer.orbglide.workers.dev'
      const res = await fetch(`${baseUrl}/api/player/${playerId}/ranking?stat=${statType}`)
      if (!res.ok) return null
      return res.json()
    },
    enabled: !!playerId,
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}
